// src/api/researchChatApi.js
//
// Research Chat surface: same SSE-over-POST protocol and same conversation
// endpoints as Aegis Chat, under /api/research-chat/*. The stream reader,
// frame parser, and auth headers are imported from generalChatApi rather than
// re-implemented, so the two surfaces cannot drift in how they read a stream --
// only in what the server does behind it.
//
// No attachment functions: this surface has no attachment endpoint (see
// research_chat/routes.py), and GeneralChatPage is rendered with
// attachmentsEnabled={false}.

import { API_BASE } from "../utils/api";
import { authHeaders, streamChatAt } from "./generalChatApi";

export async function streamResearchChat(
  { conversationId, message, allowWebSearch, deepThinking },
  handlers = {}
) {
  return streamChatAt(
    "/research-chat/chat",
    { conversationId, message, allowWebSearch, deepThinking },
    handlers
  );
}

export async function listConversations() {
  const res = await fetch(`${API_BASE}/research-chat/conversations`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`list failed (${res.status})`);
  return res.json();
}

export async function loadConversation(id) {
  const res = await fetch(
    `${API_BASE}/research-chat/conversations/${encodeURIComponent(id)}`,
    { headers: authHeaders() }
  );
  if (!res.ok) throw new Error(`load failed (${res.status})`);
  return res.json();
}

export async function renameConversation(id, title) {
  const res = await fetch(
    `${API_BASE}/research-chat/conversations/${encodeURIComponent(id)}`,
    { method: "PATCH", headers: authHeaders(true), body: JSON.stringify({ title }) }
  );
  if (!res.ok) throw new Error(`rename failed (${res.status})`);
  return res.json();
}

export async function setConversationPinned(id, pinned) {
  const res = await fetch(
    `${API_BASE}/research-chat/conversations/${encodeURIComponent(id)}`,
    { method: "PATCH", headers: authHeaders(true), body: JSON.stringify({ pinned }) }
  );
  if (!res.ok) throw new Error(`pin failed (${res.status})`);
  return res.json();
}

export async function deleteConversation(id) {
  const res = await fetch(
    `${API_BASE}/research-chat/conversations/${encodeURIComponent(id)}`,
    { method: "DELETE", headers: authHeaders() }
  );
  if (!res.ok && res.status !== 204) throw new Error(`delete failed (${res.status})`);
  return true;
}

// Recent search decisions for the signed-in user: what was proposed, whether it
// was allowed out, and what came back. Read-only by construction -- there is no
// write path to research_search_events outside the turn itself.
export async function listSearchEvents({ limit = 50, blockedOnly = false } = {}) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (blockedOnly) qs.set("blockedOnly", "1");
  const res = await fetch(`${API_BASE}/research-chat/search-events?${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`search events failed (${res.status})`);
  return res.json();
}

// Attachments are not available on this surface; the page hides the control.
async function unsupportedAttachments() {
  throw new Error("Attachments are not available on Research Chat");
}

export const RESEARCH_CHAT_API = {
  streamChat: streamResearchChat,
  listConversations,
  loadConversation,
  renameConversation,
  setConversationPinned,
  deleteConversation,
  uploadAttachments: unsupportedAttachments,
  deleteAttachment: unsupportedAttachments,
};
